import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { feedbackAPI } from '../services/api';

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(5px)' },
  card: { background: '#fff', borderRadius: 16, width: '90%', maxWidth: 440, padding: '32px 28px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)', textAlign: 'center', position: 'relative', animation: 'feedbackModalOpen 0.25s ease-out' },
  heartIconContainer: { width: 64, height: 64, borderRadius: '50%', backgroundColor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: 'var(--char)', marginBottom: 8 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 20, lineHeight: 1.5 },
  starsContainer: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 20 },
  star: { fontSize: 36, cursor: 'pointer', transition: 'transform 0.1s, color 0.1s' },
  textarea: { width: '100%', padding: '12px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, outline: 'none', minHeight: 90, resize: 'none', marginBottom: 20, transition: 'border-color 0.2s', boxSizing: 'border-box' },
  btnArea: { display: 'flex', gap: 12, justifyContent: 'center' },
  btnSubmit: { background: '#4C1D95', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'opacity 0.2s', flex: 1 },
  btnRemind: { background: '#f3f4f6', color: 'var(--char)', border: 'none', padding: '12px 20px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'background-color 0.2s', flex: 1 }
};

export default function FeedbackModal({ user }) {
  const [activePrompt, setActivePrompt] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [postponing, setPostponing] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    if (activePrompt) return; // avoid calling if already showing a prompt
    
    // Inject keyframes style once
    const styleId = 'feedback-modal-keyframes';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes feedbackModalOpen {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }

    // Call API to check for active feedback prompts
    feedbackAPI.active()
      .then(res => {
        if (res.data.success && res.data.data) {
          setActivePrompt(res.data.data);
        }
      })
      .catch(err => {
        console.error('Error checking active feedback prompts:', err);
      });
  }, [user, location.pathname, activePrompt]);

  if (!activePrompt) return null;

  const handleRatingClick = (val) => {
    setRating(val);
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('Please select a star rating first.');
      return;
    }
    setSubmitting(true);
    try {
      await feedbackAPI.respond(activePrompt._id, {
        status: 'submitted',
        rating,
        description
      });
      setActivePrompt(null);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemindLater = async () => {
    setPostponing(true);
    try {
      await feedbackAPI.respond(activePrompt._id, {
        status: 'remind_later'
      });
      setActivePrompt(null);
    } catch (err) {
      console.error('Failed to postpone feedback:', err);
      setActivePrompt(null); // dismiss anyway on error
    } finally {
      setPostponing(false);
    }
  };

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      const active = (hoverRating || rating) >= i;
      stars.push(
        <span
          key={i}
          style={{
            ...S.star,
            color: active ? '#F5A623' : '#E5E7EB',
            transform: hoverRating === i ? 'scale(1.15)' : 'scale(1)'
          }}
          onMouseEnter={() => setHoverRating(i)}
          onMouseLeave={() => setHoverRating(0)}
          onClick={() => handleRatingClick(i)}
        >
          ★
        </span>
      );
    }
    return stars;
  };

  const getEventTitle = () => {
    if (activePrompt.event === 'registration') {
      return 'How has your experience been since registering?';
    } else if (activePrompt.event === 'demand_posted') {
      return 'How was the process of posting your demands?';
    } else if (activePrompt.event === 'offer_submitted') {
      return 'How was the process of submitting offers?';
    }
    return 'We value your feedback!';
  };

  const isPending = submitting || postponing;

  return (
    <div style={S.overlay}>
      <div style={S.card}>
        <div style={S.heartIconContainer}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#EF4444" width="32" height="32">
            <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
          </svg>
        </div>
        <div style={S.title}>Share Your Feedback</div>
        <div style={S.subtitle}>{getEventTitle()} Your rating helps us make Kanjusee better.</div>

        <div style={S.starsContainer}>
          {renderStars()}
        </div>

        <textarea
          style={S.textarea}
          placeholder="Tell us what you liked or how we can improve (optional)..."
          value={description}
          onChange={e => setDescription(e.target.value)}
          disabled={isPending}
        />

        <div style={S.btnArea}>
          <button
            style={S.btnRemind}
            onClick={handleRemindLater}
            disabled={isPending}
          >
            {postponing ? 'One moment...' : 'Remind Me Later'}
          </button>
          <button
            style={{
              ...S.btnSubmit,
              opacity: rating === 0 || isPending ? 0.6 : 1,
              cursor: rating === 0 || isPending ? 'not-allowed' : 'pointer'
            }}
            onClick={handleSubmit}
            disabled={rating === 0 || isPending}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </div>
  );
}
